/**
 * Industry → Department → Role cascade for non-IQ question banks.
 * Loads master data via the same v1 endpoints used elsewhere in settings.
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { v1Get } from '@/lib/api';
import { formatMasterOptionLabel } from '@/lib/masterDataLabels';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const EMPTY_TAXONOMY = { industryId: '', departmentId: '', roleId: '' };

function isActiveRow(row) {
  return !row?.status || row.status === 'active';
}

/** Derive industry/department from a role_id using loaded master rows. */
export function taxonomyFromRole(roleId, roles = [], departments = []) {
  if (!roleId) return { ...EMPTY_TAXONOMY };
  const role = roles.find((r) => r.id === roleId);
  if (!role) return { industryId: '', departmentId: '', roleId };
  const dept = departments.find((d) => d.id === role.department_id);
  return {
    industryId: role.industry_id || dept?.industry_id || '',
    departmentId: role.department_id || '',
    roleId: role.id,
  };
}

export default function IndustryDeptRoleCascade({
  value = EMPTY_TAXONOMY,
  onChange,
  required = false,
  disabled = false,
  testIdPrefix = 'taxonomy',
}) {
  const industryId = value?.industryId || '';
  const departmentId = value?.departmentId || '';
  const roleId = value?.roleId || '';
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const { data: industries = [] } = useQuery({
    queryKey: ['v1-master-industries'],
    queryFn: () => v1Get('/master/industries'),
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['v1-master-departments'],
    queryFn: () => v1Get('/master/departments'),
  });
  const { data: roles = [] } = useQuery({
    queryKey: ['v1-master-roles'],
    queryFn: () => v1Get('/master/roles'),
  });

  // Hydrate industry/dept when only roleId is known (edit forms).
  useEffect(() => {
    if (!roleId || (industryId && departmentId) || !roles.length) return;
    const next = taxonomyFromRole(roleId, roles, departments);
    if (!next.departmentId && !next.industryId) return;
    if (next.industryId === industryId && next.departmentId === departmentId) return;
    onChangeRef.current?.(next);
  }, [roleId, industryId, departmentId, roles, departments]);

  const filteredDepts = useMemo(() => {
    const rows = departments.filter(isActiveRow);
    return industryId ? rows.filter((d) => d.industry_id === industryId) : rows;
  }, [departments, industryId]);

  const filteredRoles = useMemo(() => {
    let rows = roles.filter(isActiveRow);
    if (departmentId) rows = rows.filter((r) => r.department_id === departmentId);
    else if (industryId) rows = rows.filter((r) => r.industry_id === industryId);
    return rows;
  }, [roles, departmentId, industryId]);

  const emit = (patch) => {
    onChange?.({
      industryId,
      departmentId,
      roleId,
      ...patch,
    });
  };

  const star = required ? <span className="text-rose-600"> *</span> : null;
  const industryPlaceholder = required ? 'Select industry' : 'All industries';
  const deptPlaceholder = !industryId
    ? (required ? 'Pick industry first' : 'All departments')
    : (required ? 'Select department' : 'All departments');
  const rolePlaceholder = !departmentId
    ? (required ? 'Pick department first' : 'All roles')
    : (required ? 'Select role' : 'All roles');

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-cascade`}>
      <div>
        <label className="text-xs font-medium text-muted-foreground">
          Industry{star}
        </label>
        <Select
          value={industryId || undefined}
          onValueChange={(v) => emit({ industryId: v, departmentId: '', roleId: '' })}
          disabled={disabled}
        >
          <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-industry`}>
            <SelectValue placeholder={industryPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {industries.filter(isActiveRow).map((i) => (
              <SelectItem key={i.id} value={i.id}>{formatMasterOptionLabel(i)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">
          Department{star}
        </label>
        <Select
          value={departmentId || undefined}
          onValueChange={(v) => emit({ departmentId: v, roleId: '' })}
          disabled={disabled || !industryId}
        >
          <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-department`}>
            <SelectValue placeholder={deptPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {filteredDepts.map((d) => (
              <SelectItem key={d.id} value={d.id}>{formatMasterOptionLabel(d)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">
          Role{star}
        </label>
        <Select
          value={roleId || undefined}
          onValueChange={(v) => {
            const role = roles.find((r) => r.id === v);
            const dept = departments.find((d) => d.id === role?.department_id);
            emit({
              roleId: v,
              departmentId: role?.department_id || departmentId,
              industryId: role?.industry_id || dept?.industry_id || industryId,
            });
          }}
          disabled={disabled || !departmentId}
        >
          <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-role`}>
            <SelectValue placeholder={rolePlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {filteredRoles.map((r) => (
              <SelectItem key={r.id} value={r.id}>{formatMasterOptionLabel(r)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
